import React, { useEffect, useMemo, useState } from "react";
import { searchTenantUsers } from "../../../api/globalAdminApi";
import { ConfirmDialog, FilterChip, Notice, Panel, Steps, StatusChip } from "../ui/AdminUi";
import { I } from "../ui/icons";
import { initialsOf, roleLabelFor } from "./tenantModel";

const ROLE_FILTERS = [
  { key: "all", label: "All" },
  { key: "student", label: "Students" },
  { key: "instructor", label: "Instructors" },
];

/**
 * Impersonate a user of one training center. The steps are: choose the user, choose the role when they have several,
 * then confirm. Production never gets this panel (the parent shows the guard instead).
 */
export default function ImpersonationPanel({ tenant, impersonating, onImpersonate, onExit }) {
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [pickedId, setPickedId] = useState(null);
  const [pickedRole, setPickedRole] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!tenant?.id) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      setError(null);
      searchTenantUsers(tenant.id, query, roleFilter, controller.signal)
        .then((list) => {
          if (controller.signal.aborted) return;
          setUsers(Array.isArray(list) ? list : []);
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          setUsers([]);
          setError(err?.message || "Failed to load users.");
        })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 250);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [tenant?.id, query, roleFilter]);

  const decorated = useMemo(() => users.map((u) => ({
    ...u,
    initials: initialsOf(u.name),
    roleLabel: u.roles.map(roleLabelFor).join(" · "),
  })), [users]);

  const picked = decorated.find((u) => u.id === pickedId) || null;
  const multi = picked && picked.roles.length > 1;
  const role = picked ? (multi ? pickedRole : picked.roles[0]) : null;
  const canStart = !!picked && (!multi || !!pickedRole);
  const step = !picked ? 0 : canStart ? 2 : 1;

  if (impersonating) {
    return (
      <Panel title="Impersonation" actions={<StatusChip status="Failed" label="Session active" solid />}>
        <div className="panel-b" style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0, lineHeight: 1.55 }}>
            You are viewing {tenant.name} as <b>{impersonating.name}</b> ({impersonating.roleLabel || impersonating.role}). Every action is recorded as you acting on their behalf.
          </p>
          <div><button type="button" className="b danger" onClick={onExit}><I.x s={14} />End impersonation</button></div>
        </div>
      </Panel>
    );
  }

  const choose = (user) => {
    setPickedId(user.id);
    setPickedRole(user.roles.length === 1 ? user.roles[0] : null);
  };

  return (
    <Panel
      title="Impersonate a user"
      subtitle="You see the center exactly as this user does. Actions are recorded against your account."
      actions={<Steps steps={["User", "Role", "Start"]} current={step} />}
    >
      <div className="panel-b" style={{ display: "grid", gap: 12 }}>
        <Notice kind="warn">Every action you take while impersonating is recorded to the audit log against your admin account.</Notice>

        <label className="srch" style={{ maxWidth: "none" }}>
          <I.search />
          <input
            placeholder="Search by name or email…" value={query} aria-label="Search users"
            onChange={(e) => { setQuery(e.target.value); setPickedId(null); setPickedRole(null); }}
          />
        </label>

        <div className="tb" role="group" aria-label="Filter by role">
          {ROLE_FILTERS.map((r) => (
            <FilterChip key={r.key} label={r.label} on={roleFilter === r.key} onClick={() => { setRoleFilter(r.key); setPickedId(null); setPickedRole(null); }} />
          ))}
        </div>

        {error && <Notice kind="bad">{error}</Notice>}

        <div style={{ border: "1px solid var(--ga-line)", borderRadius: 8, maxHeight: 288, overflowY: "auto" }}>
          {loading ? (
            <div className="sub" style={{ padding: 16, textAlign: "center" }}>Loading users…</div>
          ) : decorated.length === 0 ? (
            <div className="sub" style={{ padding: 16, textAlign: "center" }}>
              {query.trim()
                ? <>No {roleFilter === "student" ? "students" : roleFilter === "instructor" ? "instructors" : "users"} match “{query}” in this training center.</>
                : <>This training center has no {roleFilter === "student" ? "students" : roleFilter === "instructor" ? "instructors" : "users"} to impersonate.</>}
            </div>
          ) : decorated.map((u, i) => {
            const on = u.id === pickedId;
            return (
              <button
                key={u.id} type="button" onClick={() => choose(u)} aria-pressed={on}
                style={{ display: "flex", gap: 10, alignItems: "center", width: "100%", padding: "10px 12px", border: 0, borderTop: i ? "1px solid var(--ga-line)" : 0, background: on ? "var(--st-info-bg)" : "none", textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit" }}
              >
                <span aria-hidden="true" style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--color-surface-press-light)", display: "grid", placeItems: "center", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>{u.initials}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ fontWeight: 600, display: "block" }}>{u.name}</b>
                  <span className="sub mono" style={{ fontSize: 12 }}>{u.email}</span>
                </span>
                <span className="sub" style={{ fontSize: 12.5, whiteSpace: "nowrap" }}>{u.roleLabel}</span>
                {on ? <span style={{ color: "var(--color-primary)" }}><I.check s={14} /></span> : <span className="sub"><I.chev s={14} /></span>}
              </button>
            );
          })}
        </div>

        {multi && (
          <div className="field">
            <span>Role to use</span>
            <div className="seg">
              {picked.roles.map((r) => (
                <button key={r} type="button" className={pickedRole === r ? "on" : ""} aria-pressed={pickedRole === r} onClick={() => setPickedRole(r)}>{roleLabelFor(r)}</button>
              ))}
            </div>
            <span className="h">{picked.name} has {picked.roles.length} roles. You will see the center as this role only.</span>
          </div>
        )}

        <div>
          <button type="button" className="b pri" disabled={!canStart} onClick={() => setConfirming(true)}>
            <I.eye s={14} />{picked ? `Start impersonation as ${picked.name.split(" ")[0]}` : "Select a user to impersonate"}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        title={picked ? `Impersonate ${picked.name}?` : "Impersonate?"}
        confirmLabel="Start session"
        consequence={picked && role ? `You will be signed in to ${tenant.name} as ${picked.name} (${roleLabelFor(role)}). Anything you change is saved to their account and logged as you acting on their behalf.` : ""}
        keeps="Their password and sessions are not affected. A red banner stays visible until you end the session."
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          onImpersonate({ ...picked, role, tenantName: tenant.name, tenantId: tenant.id });
        }}
      />
    </Panel>
  );
}
